import type { WorkOrderChangeObjectItem } from 'gql/graphql';

import { useCallback, useMemo } from 'react';

interface IUseCorrectUpdateWorkOrderChangeUerData {
    objectsData: WorkOrderChangeObjectItem[];
    workOrderObjectId: number | undefined | null;
    workOrderChangeObjectId: number | undefined | null;
    refreshWorkOrderChangeObjects: (onSuccess?: () => void) => void;
    refreshTableFn: Array<() => void> | (() => void);
}

type ICheckObjectIds = number[];

export const useCorrectUpdateWorkOrderChangeUerData = ({
    objectsData,
    workOrderObjectId,
    workOrderChangeObjectId,
    refreshWorkOrderChangeObjects,
    refreshTableFn,
}: IUseCorrectUpdateWorkOrderChangeUerData) => {
    const hasCheckedFilterObject = useMemo(
        () => !!(objectsData && (workOrderObjectId || workOrderChangeObjectId)),
        [objectsData, workOrderChangeObjectId, workOrderObjectId],
    );

    const withSelectedObjFilterNeedUpdate = useMemo(
        () => !!(objectsData && workOrderObjectId && !workOrderChangeObjectId),
        [objectsData, workOrderChangeObjectId, workOrderObjectId],
    );

    /* проверяем данные из мутации.
     * обновлять объекты нужно если (вернуть true):
     * есть id НЗ (objectIds) и хотя бы для одного из них в objectChangeIds нашелся объект без workOrderChangeObjectId
     */
    const checkNeedUpdateWithMutationData = useCallback(
        (objectIds: ICheckObjectIds = []) => {
            if (!Array.isArray(objectIds) || !objectIds?.length) {
                return false;
            }

            return objectsData?.some(
                (object) => objectIds.includes(object.workOrderObjectId) && !object.workOrderChangeObjectId,
            );
        },
        [objectsData],
    );

    const updateTableRunner = useCallback(() => {
        if (Array.isArray(refreshTableFn) && refreshTableFn.length) {
            refreshTableFn.forEach((fn) => {
                if (typeof fn === 'function') {
                    fn();
                }
            });
        } else if (typeof refreshTableFn === 'function') {
            refreshTableFn();
        }
    }, [refreshTableFn]);

    const makeCorrectRefreshTableData = useCallback(
        (objectIds: ICheckObjectIds, needUpdateOnlyObj = false) => {
            if (hasCheckedFilterObject) {
                if (withSelectedObjFilterNeedUpdate) {
                    refreshWorkOrderChangeObjects();
                } else {
                    updateTableRunner();
                }
            } else {
                const needUpdateAfterCheckMutationData = checkNeedUpdateWithMutationData(objectIds);

                if (needUpdateAfterCheckMutationData) {
                    refreshWorkOrderChangeObjects(updateTableRunner);
                } else if (!needUpdateOnlyObj) {
                    updateTableRunner();
                }
            }
        },
        [
            checkNeedUpdateWithMutationData,
            hasCheckedFilterObject,
            refreshWorkOrderChangeObjects,
            updateTableRunner,
            withSelectedObjFilterNeedUpdate,
        ],
    );

    return useMemo(
        () => ({
            makeCorrectRefreshTableData,
        }),
        [makeCorrectRefreshTableData],
    );
};
